import type React from "react"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import { ArrowRight, CheckCircle, Gauge, Weight } from "lucide-react"

const ModelsPage: React.FC = () => {
  const models = [
    {
      name: "MULTIHOG CL",
      subtitle: "Non-snow areas and light duty applications",
      description:
        "The CL tractor is a reliable choice for cities, towns, and airports needing affordable, all-around performance. It handles sanitation, light mowing, weed control, and—when paired with the CityVac Garbage Vacuum—efficient debris collection.",
      image: "/images/multihog-cl.jpg",
      specs: {
        width: '45" wide',
        power: "55 - 75 hp",
        weight: "3500 - 4000 Kg",
        mounting: "Top rear mounting for attachments and three-point hitch",
      },
      features: [
        'Compact machine (45" wide), with top rear mounting for attachments and three-point hitch',
        "Short turning radius and excellent visibility",
        "Power: 55 - 75 (hp)",
        "Gross Vehicle Weight: 3500 - 4000 Kg",
      ],
      applications: [
        "City sanitation and light mowing",
        "Airports (CityVac)",
        "Municipalities, cities and towns on tight budgets",
        "CityVac Garbage Vacuum",
        "Weed control",
      ],
      category: "Light Duty",
      featured: true,
    },
    {
      name: "MULTIHOG CX",
      subtitle: "For snow clearing, grass mowing, sweeping, pressure washing and more",
      description:
        "Tackle every job with confidence from snow removal and sweeping to grass mowing and pressure washing. This multi-functional powerhouse is built to handle it all, no matter the season.",
      image: "/images/multihog-cx.png",
      specs: {
        width: "Compact width",
        power: "55 - 75 hp",
        weight: "3500 - 4000 Kg",
        mounting: "Top rear mounting for attachments and three-point hitch",
      },
      features: [
        "Compact width for easy operation on sidewalks and in narrow areas",
        "Top rear mounting for attachments and three-point hitch",
        "Spring and damper suspension for smooth drive and ultimate operator comfort",
        "Short turning radius and excellent visibility",
        "Ergonomic joystick-controlled hydraulics for easy operation",
        "55-75 (hp) Kohler engine",
        "Gross Vehicle Weight: 3500-4000 Kg",
      ],
      applications: [
        "Sidewalks",
        "Airports (CityVac)",
        "Contractors (snow removal)",
        "Harsh winters",
        "Municipalities, cities and towns",
        "Facility maintenance (universities, schools)",
      ],
      category: "Multi-Season",
      featured: false,
    },
    {
      name: "MULTIHOG MX",
      subtitle: "For demanding environments with heavy-duty performance",
      description:
        "Engineered for demanding environments, the MX delivers 120hp and a 4,000 kg capacity in a quiet, operator-friendly design. With wide attachment compatibility and multiple cab sizes, it's ideal for airports, municipalities, and large-scale maintenance year-round.",
      image: "/images/multihog-mx.png",
      specs: {
        width: '51" wide (MXC variant)',
        power: "100 - 120 hp",
        weight: "7,000 kg GVW",
        mounting: "Front and rear mounting options",
      },
      features: [
        "Articulated, heavy-duty build for year-round tasks",
        "100-120 hp Deutz engine, Tier 4i compliant",
        "Joystick-controlled hydraulics for smooth handling",
        "360° cab visibility with A/C comfort",
        "Low noise (64 dB) and minimal vibration",
        "7,000 kg GVW for large attachments",
      ],
      applications: [
        'Large sidewalks (MXC-51")',
        "Large facility maintenance",
        "Harsh winter areas",
        "Municipalities, cities and towns",
        "Bike paths",
        "Airports",
      ],
      category: "Heavy Duty",
      featured: false,
    },
    {
      name: "MULTIHOG MH",
      subtitle: "Efficient wide coverage with rock-solid stability",
      description:
        "Engineered for efficiency, the MH delivers wide coverage, tight turning, and rock-solid stability. With a spacious cab and support for boom mowers, cold planers, and Combi Plow and Broom, it's a smart, budget-friendly choice for all-season performance.",
      image: "/images/multihog-mh.png",
      specs: {
        width: "Compact articulated",
        power: "75 hp",
        weight: "6,000 kg GVW",
        mounting: "Front and rear attachments",
      },
      features: [
        "Heavy-duty articulated build for snow, mowing, planing, hedge cutting and more",
        "75 hp Kohler engine, Tier 4 Final compliant",
        "Joystick hydraulics for smooth, easy control",
        "Spacious A/C cab with 360° visibility",
        "Compact, articulated design for tight spaces",
        "6,000 kg GVW for front and rear attachments",
      ],
      applications: [
        "Bike paths, airports and contractors",
        "Municipalities, cities and towns with tight budgets",
        "Maintenance of large facilities",
        "Using the Combi Plow and Broom unit",
        "Using a large Boom Mower unit",
        "Using a Cold Planer",
      ],
      category: "Versatile",
      featured: false,
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Multihog Tractor Range
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Choose Your Perfect
              <br />
              <span className="text-red-500">Multihog Model</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              From light-duty municipal work to heavy industrial applications, our range of Multihog tractors delivers
              unmatched versatility and performance for every need.
            </p>
          </div>
        </div>
      </section>

      {/* Models Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-12">
              {models.map((model, index) => (
                <div
                  key={model.name}
                  className={`bg-white rounded-lg shadow-lg overflow-hidden ${model.featured ? "ring-2 ring-red-500" : ""}`}
                >
                  {model.featured && (
                    <div className="bg-red-600 text-white text-center py-2 text-sm font-semibold">FEATURED MODEL</div>
                  )}
                  <div className="grid lg:grid-cols-2 gap-8 p-8">
                    {/* Model Image */}
                    <div className="relative">
                      <img
                        src={model.image || "https://placehold.co/600x400?text=Multihog+Model"}
                        alt={model.name}
                        className="w-full h-auto rounded-lg"
                      />
                      <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {model.category}
                      </div>
                    </div>

                    {/* Model Details */}
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">{model.name}</h2>
                        <p className="text-lg text-red-600 font-semibold mb-4">{model.subtitle}</p>
                        <p className="text-gray-600 leading-relaxed">{model.description}</p>
                      </div>

                      {/* Key Specs */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center">
                          <Gauge className="h-5 w-5 text-red-600 mr-2" />
                          <div>
                            <div className="text-sm text-gray-500">Power</div>
                            <div className="font-semibold">{model.specs.power}</div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Weight className="h-5 w-5 text-red-600 mr-2" />
                          <div>
                            <div className="text-sm text-gray-500">Weight</div>
                            <div className="font-semibold">{model.specs.weight}</div>
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features:</h3>
                        <ul className="space-y-2">
                          {model.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start">
                              <CheckCircle className="h-4 w-4 text-red-600 mr-2 mt-1 flex-shrink-0" />
                              <span className="text-gray-600 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Applications */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Unmatched Performance In:</h3>
                        <div className="flex flex-wrap gap-2">
                          {model.applications.map((app, idx) => (
                            <span
                              key={idx}
                              className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs border"
                            >
                              {app}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center flex-1 transition-colors">
                          Request Quote
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </button>
                        <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-medium flex-1 transition-colors">
                          View Specifications
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Compare Models</h2>
              <p className="text-lg text-gray-600">
                Not sure which Multihog is right for you? Compare specifications side by side.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold text-gray-900">Specification</th>
                    {models.map((model) => (
                      <th key={model.name} className="text-center p-4 font-semibold text-gray-900">
                        {model.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-700">Power Range</td>
                    {models.map((model) => (
                      <td key={model.name} className="p-4 text-center text-gray-600">
                        {model.specs.power}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-700">Weight Range</td>
                    {models.map((model) => (
                      <td key={model.name} className="p-4 text-center text-gray-600">
                        {model.specs.weight}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-medium text-gray-700">Width</td>
                    {models.map((model) => (
                      <td key={model.name} className="p-4 text-center text-gray-600">
                        {model.specs.width}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-4 font-medium text-gray-700">Best For</td>
                    {models.map((model) => (
                      <td key={model.name} className="p-4 text-center text-gray-600">
                        {model.category}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Find Your Perfect Multihog?</h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Our experts are here to help you choose the right model for your specific needs and applications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors">
                Speak with an Expert
              </button>
              <button className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors">
                Download Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ModelsPage
